import { GLOBAL_FILTER_FIELDS, type GlobalFilterField } from './GlobalFilterTypes';

/**
 * Single source of truth for WHERE each global filter shows — opt-in per page.
 * The filter row derives its visible fields from the current route via
 * {@link globalScopeForPath}, so a global field appears only on the pages that
 * list it here (not on every page that renders the row). Keyed by the first
 * route segment, so a surface and its drill-ins (`/usage`, `/usage/:topic`)
 * share one entry.
 *
 * Adding a new global field to {@link GLOBAL_FILTER_FIELDS} forces an entry here
 * (the Record is exhaustive), so you can't promote a field without declaring
 * where it belongs.
 */
const FIELD_PAGES: Record<GlobalFilterField, string[]> = {
    provider_connection_id: ['ai-explorer', 'behavior', 'adoption', 'governance', 'token-usage', 'cost', 'licenses'],
    user_id: ['ai-explorer', 'behavior', 'adoption', 'governance', 'token-usage', 'cost'],
};

/** Route segments that show the date-range control. */
const DATE_RANGE_PAGES: string[] = ['ai-explorer', 'behavior', 'adoption', 'governance', 'token-usage', 'cost', 'licenses'];

/** Full paths that opt OUT of the date-range control even though their segment shows it — month-anchored views (Forecast projects the current month; License Fit uses a fixed month-to-date + projection basis) where a range picker is meaningless. */
const DATE_EXCLUDED_PATHS: string[] = ['/cost/forecast', '/cost/license-fit'];

/**
 * Route segments served by the analytics OLAP gateway — they share one apply-set,
 * since the gateway folds globals into every query regardless of surface. The
 * gateway's applied fields are the union of what these pages show, so promoting a
 * field for any analytics page auto-applies it (show and apply can't drift).
 */
const ANALYTICS_SEGMENTS: string[] = ['ai-explorer', 'behavior', 'adoption', 'governance', 'token-usage', 'cost'];

export interface PageGlobalScope {
    dateRange: boolean;
    fields: GlobalFilterField[];
}

function segmentOf(pathname: string): string {
    return pathname.replace(/^\/+/, '').split('/')[0] ?? '';
}

/** The global fields a route segment exposes — the shared basis for both showing and applying them. */
export function globalFieldsForPage(segment: string): GlobalFilterField[] {
    return GLOBAL_FILTER_FIELDS.filter((field) => FIELD_PAGES[field].includes(segment));
}

/** The global filters (date + dimensions) a page shows, derived from its route. Empty on pages that opt into none. */
export function globalScopeForPath(pathname: string): PageGlobalScope {
    const segment = segmentOf(pathname);
    const normalized = pathname.replace(/\/+$/, '');
    return {
        dateRange: DATE_RANGE_PAGES.includes(segment) && !DATE_EXCLUDED_PATHS.includes(normalized),
        fields: globalFieldsForPage(segment),
    };
}

/** The global fields the analytics gateway applies — the union across analytics pages, so apply tracks show. */
export function analyticsGlobalFields(): GlobalFilterField[] {
    const fields = new Set<GlobalFilterField>();
    for (const segment of ANALYTICS_SEGMENTS) for (const field of globalFieldsForPage(segment)) fields.add(field);
    return [...fields];
}
